import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createApplication,
  getMyApplications,
  getApplication,
  updateApplicationStatus,
  deleteApplication,
} from '@/api/services/applicationsApi';
import type {
  CreateApplicationRequest,
  GetApplicationsParams,
  UpdateApplicationStatusRequest,
  GetCasesResponse,
} from '@/types/api';

/**
 * Mutation hook for creating an application for a tuition case
 * Tutors use this to apply for cases posted by parents
 * 
 * Implements Optimistic Updates:
 * - onMutate: Immediately updates UI by setting hasApplied: true
 * - onSuccess: Confirms update with server-returned data
 * - onError: Rolls back to previous state if request fails
 * 
 * Usage:
 * const mutation = useApplication();
 * mutation.mutate({ caseId: 'case123', message: 'I am interested...' });
 */
export const useApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateApplicationRequest) => createApplication(request),
    
    // Optimistic update - update UI immediately before request completes
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['cases'] });
      
      // Snapshot the previous value for rollback
      const previousCases = queryClient.getQueriesData({ queryKey: ['cases'] });
      
      // Optimistically update all 'cases' queries in cache
      queryClient.setQueriesData<GetCasesResponse['data']>(
        { queryKey: ['cases'] },
        (oldData) => {
          if (!oldData?.cases) return oldData;
          return {
            ...oldData,
            cases: oldData.cases.map((caseItem) =>
              caseItem.id === variables.caseId
                ? { ...caseItem, hasApplied: true, applicationCount: caseItem.applicationCount + 1 }
                : caseItem
            ),
          };
        }
      );
      
      // Return context with snapshot for potential rollback
      return { previousCases };
    },
    
    // Success - confirm update with exact server data
    onSuccess: (data, variables) => {
      // Use the case data returned from API (includes hasApplied: true)
      if (data && data.case) {
        queryClient.setQueriesData<GetCasesResponse['data']>(
          { queryKey: ['cases'] },
          (oldData) => {
            if (!oldData?.cases) return oldData;
            return {
              ...oldData,
              cases: oldData.cases.map((caseItem) =>
                caseItem.id === variables.caseId
                  ? data.case // Use exact server data
                  : caseItem
              ),
            };
          }
        );
      }
      
      // Invalidate cases queries to refetch with updated hasApplied status
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      
      // Also invalidate myApplications to show the new application
      queryClient.invalidateQueries({ queryKey: ['myApplications'] });
    },
    
    // Error - rollback to previous state
    onError: (_error, _variables, context) => {
      // Restore all previous query states
      if (context?.previousCases) {
        context.previousCases.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
    
    retry: 1,
  });
};

/**
 * Query hook for fetching current tutor's applications
 * Cache key: ['myApplications', params]
 * Returns paginated list of applications with case details
 */
export const useMyApplications = (params?: GetApplicationsParams) => {
  return useQuery({
    queryKey: ['myApplications', params],
    queryFn: async () => {
      const response = await getMyApplications(params);
      return response;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Query hook for fetching a single application by ID
 * Cache key: ['application', applicationId]
 */
export const useApplicationDetail = (applicationId: string) => {
  return useQuery({
    queryKey: ['application', applicationId],
    queryFn: async () => {
      return await getApplication(applicationId);
    },
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    enabled: !!applicationId, // Only run query if applicationId is provided
  });
};

/**
 * Mutation hook for updating application status
 * Parents use this to accept/reject/shortlist applications
 * Invalidates both ['myApplications'] and ['application'] caches on success
 */
export const useUpdateApplicationStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, request }: { applicationId: string; request: UpdateApplicationStatusRequest }) =>
      updateApplicationStatus(applicationId, request),
    onSuccess: () => {
      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['myApplications'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
    },
    retry: 1,
  });
};

/**
 * Mutation hook for withdrawing (deleting) an application
 * Tutors use this to withdraw their own applications
 * Invalidates both ['myApplications'] and ['application'] caches on success
 */
export const useDeleteApplicationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationId: string) => deleteApplication(applicationId),
    onSuccess: () => {
      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['myApplications'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
    },
    retry: 1,
  });
};
